<template>
  <ClientOnly>
    <div :class="['tinymce-viewer', { 'tinymce-viewer--fit': fitContainer, 'tinymce-viewer--no-x-scroll': tableFullWidth }]">
      <iframe ref="iframeRef" class="tinymce-viewer__frame" :srcdoc="srcdoc" frameborder="0" scrolling="auto" title="본문" @load="onIframeLoad" />
    </div>
  </ClientOnly>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { linkifyPlainTextUrlsInHtml, wrapUrlWithAnchorTag, openImageModal } from '~/composables/useCommon';
import { processAllTablesBorder } from '~/composables/useTinymceTableBorder';

const config = useRuntimeConfig();
const appBaseUrl = (config.app?.baseURL || '').replace(/\/$/, '') || '';

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
  noWrap: {
    type: Boolean,
    default: false,
  },
  searchKeyword: {
    type: String,
    default: '',
  },
  minHeight: {
    type: Number,
    default: 0,
  },
  fitContainer: {
    type: Boolean,
    default: false,
  },
  /**
   * iframe 본문의 테이블 가로폭을 컨테이너에 맞춰 100%로 강제 표시할지 여부
   * - 작성자가 인라인 width(예: 60%) 로 좁게 만든 표가 viewer 영역을 안 채우는 경우 사용
   * - true 시 word-break 을 break-all 대신 keep-all + overflow-wrap 으로 완화 (한 글자씩 줄바꿈 방지)
   */
  tableFullWidth: {
    type: Boolean,
    default: false,
  },
  /** comment-reference 등 좁은 영역: iframe body margin 축소 */
  compactBody: {
    type: Boolean,
    default: false,
  },
  /** iframe body margin만 0 (padding 변경 없음) */
  zeroBodyMargin: {
    type: Boolean,
    default: false,
  },
});

const iframeRef = ref(null);
const fitContainer = computed(() => props.fitContainer);

/** TinyMCE 본문 스킨만 로드 (viewer는 읽기 전용 — oxide content.css 의 점선 가이드·고특이도 규칙은 표를 망가뜨릴 수 있어 제외) */
const contentSkinCssHref = computed(() => {
  const path = `${appBaseUrl}/tinymce/skins/content/default/content.min.css`.replace(/\/+/g, '/');
  if (typeof window !== 'undefined') {
    return new URL(path, window.location.origin).href;
  }
  return path;
});

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightSearchKeyword = (html, keyword) => {
  if (!keyword || !keyword.trim()) return html;

  const trimmedKeyword = keyword.trim();
  if (!trimmedKeyword) return html;

  const protectedHighlights = [];
  let protectedHtml = html.replace(/<span\s+class="highlight"[^>]*>.*?<\/span>/gi, match => {
    protectedHighlights.push(match);
    return `___PROTECTED_HIGHLIGHT_${protectedHighlights.length - 1}___`;
  });

  const protectedTags = [];
  protectedHtml = protectedHtml.replace(/<[^>]+>/g, match => {
    protectedTags.push(match);
    return `___PROTECTED_TAG_${protectedTags.length - 1}___`;
  });

  const keywordRegex = new RegExp(`(${escapeRegExp(trimmedKeyword)})`, 'gi');
  protectedHtml = protectedHtml
    .split(/(___PROTECTED_TAG_\d+___|___PROTECTED_HIGHLIGHT_\d+___)/g)
    .map(part => {
      if (/^___PROTECTED_(TAG|HIGHLIGHT)_\d+___$/.test(part)) return part;
      return part.replace(keywordRegex, match => `<span class="highlight">${match}</span>`);
    })
    .join('');

  protectedHtml = protectedHtml.replace(/___PROTECTED_TAG_(\d+)___/g, (_, index) => protectedTags[parseInt(index, 10)]);
  protectedHtml = protectedHtml.replace(/___PROTECTED_HIGHLIGHT_(\d+)___/g, (_, index) => protectedHighlights[parseInt(index, 10)]);

  return protectedHtml;
};

function convertQuillHtml(html) {
  if (!html) return '';

  // 레거시 로직과 동일: 탭 엔티티 보호
  const protected_ = String(html).replace(/&#9;/g, '__TAB__');

  // jQuery 없이 DOM으로 파싱/변환
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${protected_}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';

  // ql-align-* -> text-align 인라인 스타일
  root.querySelectorAll('.ql-align-center').forEach(el => {
    el.style.textAlign = 'center';
    el.classList.remove('ql-align-center');
  });
  root.querySelectorAll('.ql-align-right').forEach(el => {
    el.style.textAlign = 'right';
    el.classList.remove('ql-align-right');
  });
  root.querySelectorAll('.ql-align-justify').forEach(el => {
    el.style.textAlign = 'justify';
    el.classList.remove('ql-align-justify');
  });

  // ql-indent-N li -> TinyMCE 방식 중첩 <ul>/<ol> (레거시 jQuery 스택 로직과 동일)
  const lists = Array.from(root.querySelectorAll('ul, ol'));
  for (const list of lists) {
    if (!root.contains(list)) continue;

    const tag = list.tagName.toLowerCase();
    const directLis = Array.from(list.children).filter(child => child.tagName.toLowerCase() === 'li');
    if (!directLis.length) continue;

    const items = directLis.map(li => {
      const m = (li.className || '').match(/ql-indent-(\d+)/);
      const level = m ? parseInt(m[1], 10) : 0;
      return { el: li, level };
    });
    if (!items.some(it => it.level > 0)) continue;

    const newRoot = doc.createElement(tag);
    const stack = [{ level: 0, ul: newRoot, lastLi: null }];

    for (const { el: liEl, level } of items) {
      const li = liEl.cloneNode(true);
      const cls = li.getAttribute('class') || '';
      const newClass = cls.replace(/\bql-indent-\d+\b\s*/g, '').trim();
      if (newClass) li.setAttribute('class', newClass);
      else li.removeAttribute('class');

      while (stack.length > 1 && stack[stack.length - 1].level > level) {
        stack.pop();
      }
      const top = stack[stack.length - 1];

      if (top.level === level) {
        top.ul.appendChild(li);
        top.lastLi = li;
      } else if (top.lastLi) {
        const nested = doc.createElement(tag);
        nested.appendChild(li);
        top.lastLi.appendChild(nested);
        stack.push({ level, ul: nested, lastLi: li });
      } else {
        top.ul.appendChild(li);
        top.lastLi = li;
      }
    }

    list.parentNode?.replaceChild(newRoot, list);
  }

  // ql-indent-N 이 li가 아닌 요소(단락 등): padding-left (레거시와 동일)
  for (let i = 1; i <= 9; i++) {
    root.querySelectorAll(`.ql-indent-${i}`).forEach(el => {
      if (el.tagName.toLowerCase() === 'li') return;
      el.style.paddingLeft = `${i}em`;
      el.classList.remove(`ql-indent-${i}`);
    });
  }

  // 테이블 data-row 속성 제거
  root.querySelectorAll('[data-row]').forEach(el => {
    el.removeAttribute('data-row');
  });

  // 나머지 ql-* 클래스 전부 제거
  root.querySelectorAll('*').forEach(el => {
    const classes = Array.from(el.classList || []);
    if (!classes.length) return;

    classes
      .filter(c => c.startsWith('ql-'))
      .forEach(c => {
        el.classList.remove(c);
      });

    if (!el.classList.length) {
      el.removeAttribute('class');
    }
  });

  const result = root.innerHTML;
  return result.replace(/__TAB__/g, '&#9;');
}

/** srcdoc 본문에 닫는 script 태그가 있으면 HTML 파서가 깨지므로 이스케이프 */
function escapeClosingScriptInBody(html) {
  return String(html).replace(/<\/script>/gi, '<\\/script>');
}

function markBorderlessTables(html) {
  if (!html || typeof DOMParser === 'undefined') return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return html;

  processAllTablesBorder(root);
  return root.innerHTML;
}

const processedContent = computed(() => {
  if (!props.content) return '';

  let processed = convertQuillHtml(props.content);

  if (!processed.includes('<')) {
    processed = processed.replace(/\n/g, '<br>');
  }

  processed = linkifyPlainTextUrlsInHtml(processed);
  processed = wrapUrlWithAnchorTag(processed);
  processed = markBorderlessTables(processed);

  if (props.searchKeyword) {
    processed = highlightSearchKeyword(processed, props.searchKeyword);
  }

  return processed;
});

/**
 * iframe: content.min.css + 아래 오버라이드
 * 표:
 *  - 인라인 style 로 border 가 지정된 표 → 사용자 의도(실선 등) 그대로
 *  - border="1" 등 → content.min.css 실선
 *  - border="0" + mce-item-table → content.min.css 점선
 *  - 그 외 무테두리 → .tinymce-viewer-table-guide gradient
 */
const iframeBodyExtraCss = computed(() => {
  const bodyWrapCss = props.noWrap ? 'word-break:normal;overflow-wrap:normal;white-space:nowrap;' : 'word-break:break-all;white-space:pre-wrap;';
  const listWrapCss = props.noWrap ? 'white-space:nowrap;' : 'white-space:normal;';

  let htmlOverflowCss;
  let bodyOverflowCss;
  if (props.fitContainer) {
    // iframe 고정 높이 + 본문 자연 높이 → iframe 요소 자체 스크롤(긴 표·다단 HTML 대응)
    htmlOverflowCss = 'overflow:visible;height:auto;';
    bodyOverflowCss = 'overflow:visible;height:auto;';
  } else if (props.tableFullWidth) {
    // tableFullWidth: 표를 컨테이너 폭에 맞추고 가로 스크롤 자체를 잠금
    htmlOverflowCss = 'overflow-x:hidden;overflow-y:hidden;';
    bodyOverflowCss = 'overflow-x:hidden;overflow-y:hidden;';
  } else {
    htmlOverflowCss = 'overflow-x:auto;overflow-y:hidden;';
    bodyOverflowCss = 'overflow-x:auto;overflow-y:hidden;min-width:min-content;';
  }

  // tableFullWidth 옵션: 표/셀의 인라인 width(예: col width, td width) 무시 → 컨테이너 폭에 맞춤
  const tableFullWidthCss = props.tableFullWidth
    ? `body.mce-content-body table{width:100%!important;max-width:100%!important;table-layout:auto!important;}
body.mce-content-body table col{width:auto!important;}
body.mce-content-body table td,body.mce-content-body table th{width:auto!important;min-width:0!important;max-width:100%!important;word-break:keep-all;overflow-wrap:anywhere;}`
    : '';

  const bodyMarginCss = props.zeroBodyMargin
    ? 'margin:0;'
    : props.compactBody
      ? 'margin:0;padding:4px;'
      : 'margin:1rem;';

  return `
html{${htmlOverflowCss}}
body.mce-content-body{${bodyMarginCss}${bodyOverflowCss}color: #515471;tab-size:4;text-align:left;${bodyWrapCss}font-size:14px!important;line-height:21px!important;}
body.mce-content-body p{margin:0;}
body.mce-content-body ul,body.mce-content-body ol{margin-block-start:0;margin-block-end:0;}
body.mce-content-body ul,body.mce-content-body ol,body.mce-content-body li{${listWrapCss}}
body.mce-content-body img{cursor:pointer;display:block;max-width:min(100%,calc(100vw - 8px));height:auto;box-sizing:border-box;}
body.mce-content-body pre,body.mce-content-body code{max-width:100%;box-sizing:border-box;}
body.mce-content-body table{max-width:none;box-sizing:border-box;width:auto;table-layout:auto;border-collapse:collapse;}
body.mce-content-body table td,body.mce-content-body table th{padding:.4rem;word-break:normal;white-space:normal;}
body.mce-content-body table:not([cellpadding]) td,body.mce-content-body table:not([cellpadding]) th{padding:.4rem;}
body.mce-content-body table.tinymce-viewer-table-guide{border-collapse:collapse!important;border-spacing:0!important;border:none!important;background-image:repeating-linear-gradient(to right,#bbb 0 3px,transparent 3px 6px),repeating-linear-gradient(to right,#bbb 0 3px,transparent 3px 6px),repeating-linear-gradient(to bottom,#bbb 0 3px,transparent 3px 6px),repeating-linear-gradient(to bottom,#bbb 0 3px,transparent 3px 6px)!important;background-position:left top,left bottom,left top,right top!important;background-size:100% 1px,100% 1px,1px 100%,1px 100%!important;background-repeat:no-repeat!important;}
body.mce-content-body table.tinymce-viewer-table-guide td,body.mce-content-body table.tinymce-viewer-table-guide th{border:none!important;background-image:repeating-linear-gradient(to right,#bbb 0 3px,transparent 3px 6px),repeating-linear-gradient(to bottom,#bbb 0 3px,transparent 3px 6px)!important;background-position:left bottom,right top!important;background-size:100% 1px,1px 100%!important;background-repeat:no-repeat!important;background-color:transparent!important;}
body.mce-content-body table.tinymce-viewer-table-guide caption{border:1px dashed #bbb!important;}
body.mce-content-body table[border]:not([border="0"]):not(.tinymce-viewer-table-guide):not([data-tinymce-table-border="frame"]) td:not([style*="border"]),body.mce-content-body table[border]:not([border="0"]):not(.tinymce-viewer-table-guide):not([data-tinymce-table-border="frame"]) th:not([style*="border"]){border-width:1px;border-style:solid;border-color:#ccc;}
body.mce-content-body table[data-tinymce-table-border="guide"].mce-item-table[border="0"] td,body.mce-content-body table[data-tinymce-table-border="guide"].mce-item-table[border="0"] th{border:1px dashed #bbb!important;}
${tableFullWidthCss}
.highlight{background-color:#ffcc00;}
`.replace(/\s+/g, ' ');
});

const srcdoc = computed(() => {
  const bodyHtml = escapeClosingScriptInBody(processedContent.value);
  const href = contentSkinCssHref.value;
  const styleOpen = '<sty' + 'le>';
  const styleClose = '</sty' + 'le>';
  return (
    '<!DOCTYPE html><html><head>' +
    `<link rel="stylesheet" href="${href}">` +
    styleOpen +
    iframeBodyExtraCss.value +
    styleClose +
    '</head><body class="mce-content-body">' +
    bodyHtml +
    '</body></html>'
  );
});

/** http(s) 등 실제 이동 가능한 링크 — data:/blob: 등은 이미지 모달로 처리 */
function isNavigableDocumentHref(href) {
  if (href == null || href === '') return false;
  const h = String(href).trim().toLowerCase();
  if (h.startsWith('data:')) return false;
  if (h.startsWith('blob:')) return false;
  if (h.startsWith('javascript:')) return false;
  return true;
}

/** iframe 내부 문서 클릭: 실제 URL 링크만 새 탭, 그 외 이미지는 openImageModal */
function handleIframeContentClick(e) {
  const t = e.target;
  if (!t) return;

  if (t.tagName === 'IMG') {
    const link = t.closest('a[href]');
    if (link && isNavigableDocumentHref(link.getAttribute('href'))) {
      e.preventDefault();
      window.open(link.href, '_blank');
      return;
    }
    e.preventDefault();
    openImageModal(t.src, 'max-width:100%');
    return;
  }

  const a = t.closest('a[href]');
  if (!a || !a.href) return;

  const hrefAttr = a.getAttribute('href');
  const innerImg = a.querySelector('img');

  if (innerImg && !isNavigableDocumentHref(hrefAttr)) {
    e.preventDefault();
    openImageModal(innerImg.src, 'max-width:100%');
    return;
  }

  if (!isNavigableDocumentHref(hrefAttr)) {
    e.preventDefault();
    return;
  }

  e.preventDefault();
  window.open(a.href, '_blank');
}

function attachIframeClickListener() {
  const doc = iframeRef.value?.contentDocument;
  if (!doc) return;
  doc.removeEventListener('click', handleIframeContentClick);
  doc.addEventListener('click', handleIframeContentClick);
}

function resizeIframe() {
  const el = iframeRef.value;
  if (!el) return;

  if (props.fitContainer) {
    el.style.height = '100%';
    return;
  }

  const doc = el.contentDocument;
  const body = doc?.body;
  if (!body) return;

  const bodyStyle = doc.defaultView?.getComputedStyle(body);
  const marginTop = parseFloat(bodyStyle?.marginTop || '') || 0;
  const marginBottom = parseFloat(bodyStyle?.marginBottom || '') || 0;
  const contentHeight = Math.ceil(body.scrollHeight + marginTop + marginBottom);
  el.style.height = `${Math.max(contentHeight, props.minHeight)}px`;
}

function onIframeLoad() {
  nextTick(() => {
    attachIframeClickListener();
    resizeIframe();
    requestAnimationFrame(resizeIframe);
  });
}

onBeforeUnmount(() => {
  const doc = iframeRef.value?.contentDocument;
  if (doc) {
    doc.removeEventListener('click', handleIframeContentClick);
  }
});

watch(
  () => processedContent.value,
  () => {
    nextTick(() => {
      resizeIframe();
    });
  },
);
</script>

<style scoped>
.tinymce-viewer {
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
  box-sizing: border-box;
}

.tinymce-viewer--fit {
  overflow: hidden;
}

.tinymce-viewer--fit .tinymce-viewer__frame {
  height: 100%;
  overflow: auto;
}

.tinymce-viewer--no-x-scroll {
  overflow-x: hidden;
}

.tinymce-viewer--no-x-scroll .tinymce-viewer__frame {
  overflow-x: hidden;
}

.tinymce-viewer__frame {
  width: 100%;
  border: none;
  display: block;
  height: auto;
  overflow-x: auto;
  overflow-y: hidden;
  min-height: 1em;
}
</style>
